import React, { useState, useEffect } from "react";
import { LogIn, ArrowRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import { inr } from "../lib/format";
import { exposureDelta } from "../lib/exposure";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import AuthModal from "./AuthModal";
import { SlipSheet, StakeBox, SlipChips, BatIcon } from "./SlipSheet";


export default function BetSlip({ selection, liveOdds, outcomes, onClear }) {
  const { isAuthed, balance, placeBet, bets } = useApp();
  const { toast } = useToast();
  const [stake, setStake] = useState("");
  const [acceptChanges, setAcceptChanges] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [placing, setPlacing] = useState(false);

  useEffect(() => { if (selection) { setStake(""); setAcceptChanges(true); } }, [selection?.selectionId, selection?.marketKey]);

  if (!selection) return null;

  const bookedOdds = selection.odds;                 // odds when the user tapped
  const current = liveOdds != null ? liveOdds : bookedOdds; // latest backend odds
  const changed = Math.abs(current - bookedOdds) > 0.001;
  const oddsToUse = acceptChanges ? current : bookedOdds;
  const stakeNum = Number(stake) || 0;
  const isLay = selection.side === "lay";
  // Lagai: risk=stake, profit=stake*(rate-1). Khai: profit=stake, liability=stake*(rate-1).
  const risk = isLay ? stakeNum * (oddsToUse - 1) : stakeNum;
  const profit = isLay ? stakeNum : stakeNum * (oddsToUse - 1);
  const potential = risk + profit;
  // Net-exposure preview: only the INCREMENTAL worst-case loss is locked. An
  // opposite/hedging bet can free funds instead (delta < 0), so never double-lock.
  const existingMkt = (bets || []).filter(
    (b) => b.matchId === selection.matchId && b.marketName === selection.marketName && b.status === "matched"
  );
  const newBet = { selectionLabel: selection.selectionLabel, side: selection.side || "back", stake: stakeNum, odds: oddsToUse, risk, profit };
  const { delta } = exposureDelta(existingMkt, newBet, outcomes);
  const lock = Math.max(0, Math.round(delta));
  const free = Math.max(0, Math.round(-delta));
  // if odds changed and user hasn't opted to accept, block until they accept
  const blockedByChange = changed && !acceptChanges;

  const confirm = async () => {
    if (!isAuthed) { setAuthOpen(true); return; }
    if (stakeNum <= 0) { toast({ title: "Enter a stake amount" }); return; }
    if (lock > balance) { toast({ title: "Insufficient balance", description: `This ${isLay ? "Khai" : "Lagai"} bet locks ${inr(lock)} of exposure — top up to continue.` }); return; }
    if (blockedByChange) { toast({ title: "Odds changed", description: "Tick 'Accept odds changes' to place at the live price." }); return; }
    setPlacing(true);
    const res = await placeBet({
      matchId: selection.matchId,
      matchLabel: selection.matchLabel,
      marketName: selection.marketName,
      selectionLabel: selection.selectionLabel,
      odds: oddsToUse,
      stake: stakeNum,
      side: selection.side || "back",
      acceptOddsChange: acceptChanges,
    });
    setPlacing(false);
    if (res.ok) {
      toast({ title: `Bet matched ✅ · Ref ${res.ref || ""}`.trim(), description: `${isLay ? "Khai" : "Lagai"} ${selection.selectionLabel} @ ${oddsToUse.toFixed(2)}` });
      onClear();
    } else {
      toast({ title: "Could not place bet", description: res.error });
    }
  };

  const confirmLabel = !isAuthed
    ? "Sign in to bet"
    : (free > 0
        ? `Confirm ${isLay ? "Khai" : "Lagai"} · Frees ${inr(free)}`
        : `Confirm ${isLay ? "Khai" : "Lagai"} · Locks ${inr(lock)}`);

  const sideCls = isLay ? "bg-[#F994BA] text-slate-900" : "bg-[#72BBEF] text-slate-900";

  return (
    <>
      <SlipSheet onClose={onClear} isAuthed={isAuthed} balance={balance} testid="betslip-sheet" prefix="betslip"
        cta={isAuthed ? (placing ? "Placing…" : confirmLabel) : <><LogIn className="h-4 w-4 mr-1" /> Login to place bet</>}
        ctaDisabled={placing} onConfirm={confirm} confirmTestid="betslip-confirm" balanceTestid="betslip-balance">
        <div className="rounded-xl bg-white ring-1 ring-slate-200 shadow-sm overflow-hidden">
          <div className={`h-1 ${isLay ? "bg-[#F994BA]" : "bg-[#72BBEF]"}`} />
          <div className="p-2.5">
            <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
              <span className="grid place-items-center h-4 w-4 rounded-full bg-emerald-500 text-white"><BatIcon className="h-2.5 w-2.5" /></span>
              {selection.matchLabel}
            </div>
            <div className="mt-1 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-extrabold text-[13px] text-slate-900 truncate">{selection.selectionLabel}</div>
                <div className="text-[10px] text-slate-500">{selection.marketName}</div>
              </div>
              <span className={`shrink-0 text-[9px] font-extrabold uppercase rounded px-1.5 py-0.5 ${sideCls}`}>{isLay ? "Khai · Lay" : "Lagai · Back"}</span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div>
                <div className="text-[9px] uppercase tracking-wider text-slate-500">Odds</div>
                <div className="font-display font-extrabold text-xl text-slate-900 tabular-nums leading-none mt-0.5">{oddsToUse.toFixed(2)}</div>
                {changed && (
                  <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold ${current > bookedOdds ? "text-emerald-600" : "text-red-500"}`}>
                    {bookedOdds.toFixed(2)} <ArrowRight className="h-3 w-3" /> {current.toFixed(2)} live
                  </div>
                )}
              </div>
              <StakeBox value={stake} onChange={(e) => setStake(e.target.value)} testid="betslip-stake" />
            </div>
            <SlipChips stake={stake} setStake={setStake} prefix="betslip-quick" />
          </div>
        </div>

        <label className="flex items-center gap-2 rounded-xl bg-white ring-1 ring-slate-200 px-2.5 py-1.5 cursor-pointer">
          <Checkbox checked={acceptChanges} onCheckedChange={(v) => setAcceptChanges(!!v)} data-testid="betslip-accept-changes" />
          <span className="text-[11px] text-slate-700"><span className="font-semibold">Accept odds changes</span> · matched at the live price if odds move.</span>
        </label>

        <div className="rounded-xl bg-white ring-1 ring-slate-200 px-2.5 py-1 text-[12px] divide-y divide-slate-100">
          <div className="flex justify-between py-1"><span className="text-slate-600">{isLay ? "Liability (at risk)" : "Total Stake"}</span><span className="font-bold text-slate-900 tabular-nums">{inr(Math.round(risk))}</span></div>
          <div className="flex justify-between py-1"><span className="text-slate-600">Potential Win</span><span className="font-display font-extrabold text-slate-900 tabular-nums" data-testid="betslip-return">{inr(Math.round(potential))}</span></div>
        </div>

        {blockedByChange && (
          <div className="text-[11px] text-amber-800 bg-amber-50 ring-1 ring-amber-200 rounded-lg p-1.5 text-center">
            Odds changed to {current.toFixed(2)} — tick "Accept odds changes" to continue.
          </div>
        )}
      </SlipSheet>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}
